import './TeachingAbsenceEntry.scss';

import { MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
  CheckmarkRegular,
  CircleRegular,
  DismissRegular,
  RecordRegular,
} from '@fluentui/react-icons';
import { getSortedUserLabel, UserListSorting } from '@mnspro-cloud/services';
import { AbsenceResponse, AbsenceState } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps } from '../../../Button';
import { Loader } from '../../../Loader/Loader';
import { Tag, TagColorEnum, TagProps } from '../../../Tags/Tags';
import {
  getTeachingUnitTypeForAbsenceReason,
  TeachingUnitEntryIconColor,
  TeachingUnitEntryType,
} from './TeachingUnitEntry';

type ActionItemProps = ButtonProps & {
  'aria-label'?: string;
};

export interface MultiLessonAbsenceCheckboxProps {
  checked: boolean;
  timeSlotLabel?: string;
  timeSlotHeader?: string;
  disabled: boolean;
  onChecked: (checked: boolean) => void;
}

export interface TeachingAbsenceEntryProps {
  updating?: boolean;
  preferenceSorting?: UserListSorting;
  multiLessonAbsenceCheckbox?: MultiLessonAbsenceCheckboxProps[];
  actions?: ActionItemProps[];
  readonly?: boolean;
  tags?: TagProps[];
  onClick?: () => void;
  absenceResponse: AbsenceResponse;
  /**
   * Note stored for the absence (the teacher's free-text note). Rendered as an
   * extra line below the subheadline when present. Used in the class register
   * so the absence note is visible in the lesson context (work item #9830).
   */
  note?: string;
}

export const TeachingAbsenceEntry: React.FC<TeachingAbsenceEntryProps> = ({
  absenceResponse,
  multiLessonAbsenceCheckbox,
  tags,
  actions,
  readonly,
  updating,
  preferenceSorting,
  onClick,
  note,
}) => {
  const { t } = useTranslation();
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const iconColor = useMemo(() => {
    switch (getTeachingUnitTypeForAbsenceReason(absenceResponse.absenceReason?.category)) {
      case TeachingUnitEntryType.Absence:
        return TeachingUnitEntryIconColor.Red;

      case TeachingUnitEntryType.AbsenceEarly:
      case TeachingUnitEntryType.AbsenceLate:
        return TeachingUnitEntryIconColor.Yellow;

      case TeachingUnitEntryType.Present:
        return TeachingUnitEntryIconColor.Green;

      default:
        return TeachingUnitEntryIconColor.Primary;
    }
  }, [absenceResponse.absenceReason?.category]);

  const name = getSortedUserLabel(
    absenceResponse.affectedStudent,
    preferenceSorting ?? UserListSorting.FirstName,
  );

  return (
    <div className="flex flex-col items-stretch teachingAbsenceEntry">
      {readonly && (
        <MessageBar intent="warning" className="readonlyAlert">
          <MessageBarBody>
            {t('classbook:courseoverview:absence_timeslot_biggerthan_lesson', {
              defaultValue:
                'Die folgende Absenz hat einen längeren Zeitraum als die ausgewählte(n) Stunde(n), dementsprechend kann diese Absenz nicht angepasst werden.',
            })}
          </MessageBarBody>
        </MessageBar>
      )}
      <div className="flex flex-1">
        {/* Spalte 1 */}
        <div className="entryColumnOne">
          {multiLessonAbsenceCheckbox?.some((a) => a.timeSlotHeader || a.timeSlotLabel) && (
            <div className="placeHolderHeader"></div>
          )}
          <div className="flex entryWrapper">
            <div className={cn('iconWrapper', iconColor.toString())}>
              {absenceResponse.state !== AbsenceState.present && <DismissRegular />}
              {absenceResponse.state === AbsenceState.present && <CheckmarkRegular />}
            </div>
            <div className="teachingAbsenceEntryContent">
              <div className="teachingAbsenceEntryHeadline">{name}</div>

              {absenceResponse.description !== '' && (
                <div className="subheadline">{absenceResponse.description}</div>
              )}

              {note?.trim() && (
                <div className="absenceNote">
                  <span className="absenceNoteLabel">
                    {t('classbook:courseoverview:absence_note_label', {
                      defaultValue: 'Notiz:',
                    })}
                  </span>{' '}
                  {note}
                </div>
              )}

              {tags !== undefined && tags.length > 0 && (
                <div className="tagWrapper">
                  {tags.map((tag) => (
                    <Tag
                      key={`${tag.color}_${tag.label}`}
                      color={tag.color ?? TagColorEnum.Grey}
                      label={tag.label}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="actionsWrapper">
              {!updating &&
                actions &&
                actions.map((action, index) => (
                  <Button {...action} key={index} appearance="transparent" />
                ))}
              {updating && <Loader />}
            </div>
          </div>
        </div>
        {/* Spalte 2 und 3  */}
        {multiLessonAbsenceCheckbox && multiLessonAbsenceCheckbox.length > 1 && (
          <div className="entryColumnTwoAndThree">
            {multiLessonAbsenceCheckbox.map((a, index) => (
              <div className="checkboxWrapper" key={`${a.timeSlotHeader}${index}`}>
                <div className="flex flex-col checkbox">
                  {(a.timeSlotHeader || a.timeSlotLabel) && (
                    <div className="header">
                      {a.timeSlotHeader && (
                        <span className="timeSlotHeader">{a.timeSlotHeader}</span>
                      )}
                      {a.timeSlotLabel && <span>{a.timeSlotLabel}</span>}
                    </div>
                  )}
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={a.checked}
                    aria-label={t('classbook:courseoverview:absence_checkbox', {
                      defaultValue: 'Absenz auswählen',
                    })}
                    className={a.checked ? 'checkedBox' : 'uncheckedBox'}
                    disabled={a.disabled || readonly}
                    onClick={() => a.onChecked(!a.checked)}
                    onKeyDown={(event) => {
                      if (event.key === ' ') {
                        event.preventDefault();
                        a.onChecked(!a.checked);
                      }
                    }}
                  >
                    {a.checked ? (
                      <RecordRegular aria-hidden="true" />
                    ) : (
                      <CircleRegular aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Spalte 4 */}
        {actions && actions.length > 0 && (
          <div className="entryColumnFour">
            {multiLessonAbsenceCheckbox?.some((a) => a.timeSlotHeader || a.timeSlotLabel) && (
              <div className="placeHolderHeader"></div>
            )}
            <div className="flex entryWrapper">
              <div
                className={cn('iconWrapper', iconColor.toString())}
                onClick={handleClick}
                role="button"
                tabIndex={0}
                aria-label={t('classbook:courseoverview:absence_status_icon', {
                  defaultValue: 'Absenzstatus: {{status}}',
                  status:
                    absenceResponse.state === AbsenceState.present
                      ? t('classbook:courseoverview:present', { defaultValue: 'Anwesend' })
                      : t('classbook:courseoverview:absent', { defaultValue: 'Abwesend' }),
                })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                  }
                }}
              ></div>
              <div className="actionsWrapper">
                {!updating &&
                  actions &&
                  actions.map((action, index) => (
                    <Button
                      {...action}
                      key={index}
                      appearance="transparent"
                      aria-label={action['aria-label']}
                    />
                  ))}
                {updating && <Loader />}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
